import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { YAMLParser } from '../services/yamlParser';
import { FlowDefinition } from '../types';

interface YAMLUploaderProps {
  onFlowAdded: (flow: FlowDefinition) => void;
  apiKey: string;
}

export const YAMLUploader: React.FC<YAMLUploaderProps> = ({ onFlowAdded, apiKey }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setError(null);
    
    for (const file of acceptedFiles) {
      setIsProcessing(true);
      const reader = new FileReader();
      
      reader.onload = async () => {
        try {
          if (!apiKey) {
            throw new Error('Please enter your Gemini API key in Settings (gear icon)');
          }
          const yamlContent = reader.result as string;
          const flow = await YAMLParser.yamlToFlowDefinition(yamlContent, file.name, apiKey);
          onFlowAdded(flow);
          setIsProcessing(false);
        } catch (error) {
          console.error('Error parsing YAML:', error);
          setError(error instanceof Error ? error.message : 'Failed to parse YAML file');
          setIsProcessing(false);
        }
      };
      
      reader.onerror = () => {
        setError('Failed to read file');
        setIsProcessing(false);
      };
      
      reader.readAsText(file);
    }
  }, [onFlowAdded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/yaml': ['.yaml', '.yml']
    },
    multiple: true,
    disabled: isProcessing
  });

  return (
    <div className="yaml-uploader">
      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''} ${isProcessing ? 'processing' : ''}`}>
        <input {...getInputProps()} />
        {isProcessing ? (
          <>
            <i className="fa-solid fa-spinner fa-spin"></i>
            <p>Processing with AI... This may take 10-20 seconds</p>
          </>
        ) : (
          <>
            <i className="fa-solid fa-cloud-arrow-up"></i>
            {isDragActive ? (
              <p>Drop YAML files here...</p>
            ) : (
              <p>Drag & drop YAML files here, or click to select</p>
            )}
          </>
        )}
      </div>
      {error && (
        <div className="error-message">
          <i className="fa-solid fa-exclamation-triangle"></i>
          {error}
        </div>
      )}
    </div>
  );
};
